import React, { useState, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ContentCopy, DoneAll, ChevronRight, VpnKey } from "@mui/icons-material";
import { message, Steps, Button, Modal, Input, Spin } from "antd";
import { TopBar } from "../../components/CommonComponent/Topbar";
import "./Documentation.css";
import Loader from "../../components/common/Loader";

const Documentation = () => {
  const [copiedEndpoints, setCopiedEndpoints] = useState({});
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [modules, setModules] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const token = localStorage.getItem("token");
  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      fetchEndpoints(selectedModule);
    }
  }, [selectedModule]);

  const fetchModules = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/api/developer/docs/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setModules(data.message || []);
        if (data.message && data.message.length > 0) {
          setSelectedModule(data.message[0]);
        }
      } else {
        message.error("Failed to fetch modules");
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const fetchEndpoints = async (moduleName) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/api/developer/docs/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ module: moduleName }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result?.message) {
          setModuleInfo({
            name: result.message.name,
            overview: result.message.overview,
            baseUrl: result.message.baseUrl
          });
        }
        

        if (result?.message?.endpoints && Array.isArray(result.message.endpoints)) {
          const transformedEndpoints = result.message.endpoints.map((item, i) => ({
            id: i,
            name: item.name,
            description: item.description,
            Method: item.method, // Map 'method' to 'Method'
            url: `${result.message.baseUrl}${item.path}`, // Combine baseUrl and path
            activeResponseCode: 200,
            Authorization: item.name =="Login"?false :true,
            type :item.requestBody?.type === "json" ? "JSON" :"FormData",
            data: item.requestBody?.fields || {},
            params: item.queryParams || {},
            responses: [
              {
                code: 200,
                body: item.response || {}
              }
            ],
            curlCommand: item.curlCommand
          }));
          
          setEndpoints(transformedEndpoints);
          if (transformedEndpoints.length > 0) {
            setSelectedEndpoint(0);
          }
        } else {
          setEndpoints([]);
        }
      } else {
        message.error("Failed to fetch endpoints");
      }
    } catch (error) {
      console.error("Error fetching endpoints:", error);
      // message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyText = (id, text) => {
    setCopiedEndpoints((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setCopiedEndpoints((prev) => ({ ...prev, [id]: false }));
    }, 2000);
  };

  const handleResponseCodeChange = (endpointId, code) => {
    setEndpoints((prevEndpoints) =>
      prevEndpoints.map((endpoint) =>
        endpoint.id === endpointId
          ? { ...endpoint, activeResponseCode: code }
          : endpoint
      )
    );
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET":
        return "text-green-700 bg-green-50 border-green-200";
      case "POST":
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "PUT":
        return "text-purple-700 bg-purple-50 border-purple-200";
      case "DELETE":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const generateApiKey = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ROOT}/api/developer/generate-api-key/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();

        setGeneratedApiKey(data?.message);
        setApiKeyModalVisible(true);
      }
      
    
    
    } catch (error) {
      console.error("Error generating API key:", error);
      message.error("Failed to generate API key");
    }
  };

  return (
    <div className="documentation-container">
      <TopBar text="Documentation" />
      
      {/* API Key Button Row */}
      <div className="px-4 py-2 mt-2 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-medium">API Documentation</h2>
        </div>
        <button 
          type="primary" 
          icon={<VpnKey />} 
          onClick={generateApiKey}
          className="primary px-3 py-2"
        >
          Generate API Key
        </button>
      </div>
      
      <div className="flex h-full mt-3">
        <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-xl font-medium mb-4">Conversational API</h2>
            <div className="space-y-1">
              {modules.map((module, index) => (
                <button
                  key={index}
                  className={`w-full text-left p-2 rounded-md transition-colors ${
                    selectedModule === module
                      ? "bg-[#7FB04F] bg-opacity-10 text-[#7FB04F]"
                      : "hover:bg-gray-100 hover:text-[#7FB04F]"
                  }`}
                  onClick={() => setSelectedModule(module)}
                >
                  {module}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="w-80 border-r border-gray-200 overflow-y-auto">
          {/* Module Info */}
          {moduleInfo && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-xl font-medium mb-2">{moduleInfo.name}</h3>
              <p className="text-sm  mb-3">{moduleInfo.overview}</p>
              <div className="mb-2">
                <span className="text-xl font-medium">Base URL:</span>
                <div className="flex items-center justify-between mt-1 bg-gray-100 p-3 rounded-sm">
                  <code className="text-sm">{moduleInfo.baseUrl}</code>
                  <CopyToClipboard
                    text={moduleInfo.baseUrl}
                    onCopy={() => handleCopyText('baseUrl', moduleInfo.baseUrl)}
                  >
                    <button className="text-gray-500 hover:text-gray-700">
                      {copiedEndpoints['baseUrl'] ? (
                        <DoneAll fontSize="small" className="text-[#7FB04F]" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </button>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          )}
          
          <div className="p-4">
            <h3 className="font-medium text-xl  mb-3">
              Endpoints
            </h3>
            <div className="space-y-1">
              {endpoints.map((endpoint, index) => (
                <button
                  key={index}
                  className={`w-full text-left p-2 rounded-md flex items-center justify-between transition-colors ${
                    selectedEndpoint === index
                      ? "bg-[#7FB04F] bg-opacity-10 text-[#7FB04F]"
                      : "hover:bg-gray-100 hover:text-[#7FB04F]"
                  }`}
                  onClick={() => setSelectedEndpoint(index)}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-block px-2 py-1 text-sm font-medium rounded ${getMethodColor(
                        endpoint.Method
                      )}`}
                    >
                      {endpoint.Method}
                    </span>
                    <span className="truncate">{endpoint.name}</span>
                  </div>
                  <ChevronRight
                    fontSize="small"
                    className={selectedEndpoint === index ? "opacity-100" : "opacity-0"}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6  bg-white">
          {selectedEndpoint !== null && endpoints[selectedEndpoint] ? (
            <div className="w-full">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`inline-block px-2 py-1 text-xs font-medium rounded ${getMethodColor(
                      endpoints[selectedEndpoint].Method
                    )}`}
                  >
                    {endpoints[selectedEndpoint].Method}
                  </span>
                  <h1 className="text-2xl font-medium">
                    {endpoints[selectedEndpoint].name}
                  </h1>
                </div>
                <p className="">
                  {endpoints[selectedEndpoint].description || "No description available"}
                </p>
              </div>

              {/* Endpoint URL */}
              <div className="mb-8">
                <h2 className="text-xl font-medium mb-2">Endpoint</h2>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md border border-gray-200">
                  <code className="text-sm">{endpoints[selectedEndpoint].url}</code>
                  <CopyToClipboard
                    text={endpoints[selectedEndpoint].url}
                    onCopy={() => handleCopyText(`url-${selectedEndpoint}`, endpoints[selectedEndpoint].url)}
                  >
                    <button className="text-gray-500 hover:text-gray-700">
                      {copiedEndpoints[`url-${selectedEndpoint}`] ? (
                        <DoneAll fontSize="small" className="text-[#7FB04F]" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </button>
                  </CopyToClipboard>
                </div>
              </div>

              {/* Authorization */}
              {endpoints[selectedEndpoint].Authorization && (
                <div className="mb-8">
                  <h2 className="text-xl font-medium mb-2">Authorization</h2>
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <div className="flex justify-between mt-2">
                      <span className="font-medium">X-API-KEY</span>
                      <code>{"********************************"}</code>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Body */}
              {endpoints[selectedEndpoint].data && Object.keys(endpoints[selectedEndpoint].data).length > 0 && (
                <div className="mb-8">
                  <div className="flex justify-between">
                  <h2 className="text-xl font-medium mb-2">Request Body</h2> 
                  <p className="font-medium">Request Type : {endpoints[selectedEndpoint].type}</p>
                    </div>
                 
                  <div className="relative bg-gray-50 p-4 rounded-md border border-gray-200">
                    <CopyToClipboard
                      text={JSON.stringify(endpoints[selectedEndpoint].data, null, 2)}
                      onCopy={() => handleCopyText(`data-${selectedEndpoint}`, JSON.stringify(endpoints[selectedEndpoint].data))}
                    >
                      <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                        {copiedEndpoints[`data-${selectedEndpoint}`] ? (
                          <DoneAll fontSize="small" className="text-[#7FB04F]" />
                        ) : (
                          <ContentCopy fontSize="small" />
                        )}
                      </button>
                    </CopyToClipboard>
                    <pre className="text-sm overflow-x-auto">
                      {JSON.stringify(endpoints[selectedEndpoint].data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Request Parameters */}
              {endpoints[selectedEndpoint].params && Object.keys(endpoints[selectedEndpoint].params).length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-medium mb-2">Query Parameters</h2>
                  <div className="relative bg-gray-50 p-4 rounded-md border border-gray-200">
                    <CopyToClipboard
                      text={JSON.stringify(endpoints[selectedEndpoint].params, null, 2)}
                      onCopy={() => handleCopyText(`params-${selectedEndpoint}`, JSON.stringify(endpoints[selectedEndpoint].params))}
                    >
                      <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                        {copiedEndpoints[`params-${selectedEndpoint}`] ? (
                          <DoneAll fontSize="small" className="text-[#7FB04F]" />
                        ) : (
                          <ContentCopy fontSize="small" />
                        )}
                      </button>
                    </CopyToClipboard>
                    <pre className="text-sm overflow-x-auto">
                      {JSON.stringify(endpoints[selectedEndpoint].params, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Responses */}
              {endpoints[selectedEndpoint].responses && endpoints[selectedEndpoint].responses.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-medium mb-2">Responses</h2>
                  <div className="flex gap-2 mb-3">
                  
                  </div>
                  {endpoints[selectedEndpoint].responses
                    .filter((response) => response.code === endpoints[selectedEndpoint].activeResponseCode)
                    .map((response) => (
                      <div key={response.code} className="relative bg-gray-50 p-4 rounded-md border border-gray-200">
                        <CopyToClipboard
                          text={JSON.stringify(response.body, null, 2)}
                          onCopy={() => handleCopyText(`response-${selectedEndpoint}-${response.code}`, JSON.stringify(response.body))}
                        >
                          <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                            {copiedEndpoints[`response-${selectedEndpoint}-${response.code}`] ? (
                              <DoneAll fontSize="small" className="text-[#7FB04F]" />
                            ) : (
                              <ContentCopy fontSize="small" />
                            )}
                          </button>
                        </CopyToClipboard>
                        <pre className="text-sm overflow-x-auto">
                          {JSON.stringify(response.body, null, 2)}
                        </pre>
                      </div>
                    ))}
                </div>
              )}

              {/* cURL Example */}
              <div className="mb-8">
                <h2 className="text-xl font-medium mb-2">Example Request</h2>
                <div className="relative bg-gray-900 text-white p-4 rounded-md">
                  <CopyToClipboard
                    text={endpoints[selectedEndpoint].curlCommand || ""}
                    onCopy={() => handleCopyText(`curl-${selectedEndpoint}`, endpoints[selectedEndpoint].curlCommand)}
                  >
                    <button className="absolute top-3 right-3 text-gray-400 hover:text-white">
                      {copiedEndpoints[`curl-${selectedEndpoint}`] ? (
                        <DoneAll fontSize="small" className="text-green-400" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </button>
                  </CopyToClipboard>
                  <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
                    {endpoints[selectedEndpoint].curlCommand || "No example available"}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              {loading ? (
                <Spin size="large" className="custom-spin" />
              ) : (
                <p className="text-gray-500">Select an endpoint from the sidebar</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* API Key Modal */}
      <Modal
        title="Your API Key"
        open={apiKeyModalVisible}
        onCancel={() => setApiKeyModalVisible(false)}
        footer={[
          <CopyToClipboard
            key="copy"
            text={generatedApiKey}
            onCopy={() => {
              handleCopyText('apiKey', generatedApiKey);
              message.success('API key copied to clipboard');
            }}
          >
            <button type="primary" className="primary px-3 py-2.5">
              {copiedEndpoints['apiKey'] ? 'Copied!' : 'Copy API Key'}
            </button>
          </CopyToClipboard>,
          <button key="close" className="secondary px-3 py-2 ml-3" onClick={() => setApiKeyModalVisible(false)}>
            Close
          </button>
        ]}
      >
        <div className="my-4">
          <Input.Password
                style={{
                  height: "40px",
                  borderRadius: "0px", // or whatever rounded you want
                  outline: "none",
                  boxShadow: "none",
                  borderColor: "#d9d9d9", // default border color
                }}
            className="mt-6 w-full  custom-outline"
            value={generatedApiKey}
            readOnly
          />
        </div>
      </Modal>
    </div>
  );
};

export default Documentation;